import type { BenLanguageBackendCode } from '@ben/common/languages';
import type { OutputValueType } from '@ben/orion/utils/calculated-value';
import type { CustomComponentTypes } from '../constants/custom-component-types';
import type { DefaultDeductionSourceEnum } from './benefit';
import type {
  StartsOnEnum,
  WindowTypeEnum,
  WindowSubTypeEnum,
} from './modular-benefit';
import type { RestrictionSet } from './restriction-set';

export enum PolicyNumberType {
  GROUP_MEMBERSHIP_NUMBER = 'GROUP_MEMBERSHIP_NUMBER',
  POLICY_NUMBER = 'POLICY_NUMBER',
  GROUP_AND_POLICY_NUMBER = 'GROUP_AND_POLICY_NUMBER',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  TO_BE_CONFIRMED = 'TO_BE_CONFIRMED',
}

export interface IModularBenefitsListItem {
  id: string;
  name: string;
  employer: string;
  active: boolean;
}

export interface IComponent {
  id?: number;
  rank: number;
  key: string;
  name: string;
  type: CustomComponentTypes;
  description: string;
  parentId?: number;
  parentType?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any[];
  subForms?: ISubForms[];
  showName: boolean;
  /** When true, the component is hidden from employees in the Employee App. Hiding is enforced server-side; it stays visible and editable in Columbia and is still used by payroll and document templates. Defaults to false. */
  hideForEmployee?: boolean;
  /** When true, the component is hidden from the ECC (Houston admin). Hiding is enforced server-side; it stays visible in the Employee App (unless also hidden there) and is still used by payroll and document templates. Defaults to false. */
  hideForEcc?: boolean;
  /** True when component has been imported from Airtable – not used by the backend */
  importedFromAirtable?: boolean;
  disabled?: boolean;
}

export type RestrictionsByType = Partial<
  Record<
    'spouse' | 'child' | 'friend' | 'other',
    {
      minAge?: number;
      maxAge?: number;
      maxSelections?: number;
    }
  >
>;

interface IField extends IComponent {
  key: string;
  required: boolean;
}

export interface IStep {
  id?: number;
  name: string;
  rank: number;
  components: (IComponent | IField)[];
  tempId?: string;
}

export interface ISubPrice {
  id?: number;
  name: string;
  rank: number;
  pricingFormula: string;
  employeeContribution?: number;
  maxEmployerContributionAmount?: number;
}

export type IRateTableData = Record<string, string | number | boolean>;

export interface IModularBenefitRateTable {
  id?: number;
  key: string;
  data: IRateTableData[];
  isFallback: boolean;
  effectiveFrom?: string;
}

export enum DynamicEnrollmentEndDate {
  AT_24_MONTHS_FROM_START_DATE = 'AT_24_MONTHS_FROM_START_DATE',
  AT_12_MONTHS_FROM_START_DATE = 'AT_12_MONTHS_FROM_START_DATE',
  AT_6_MONTHS_FROM_START_DATE = 'AT_6_MONTHS_FROM_START_DATE',
  AT_4_MONTHS_FROM_START_DATE = 'AT_4_MONTHS_FROM_START_DATE',
  AT_3_MONTHS_FROM_START_DATE = 'AT_3_MONTHS_FROM_START_DATE',
  AT_2_MONTHS_FROM_START_DATE = 'AT_2_MONTHS_FROM_START_DATE',
  AT_1_MONTH_FROM_START_DATE = 'AT_1_MONTH_FROM_START_DATE',
  AT_CURRENT_YEAR_END = 'AT_CURRENT_YEAR_END',
  AT_LOAN_TERM_END = 'AT_LOAN_TERM_END',
}

// we should move this to orval eventually
type BannerHeaderFields = Partial<
  Record<`bannerHeader${BenLanguageBackendCode}`, string>
>;

type BannerBodyFields = Partial<
  Record<`bannerBody${BenLanguageBackendCode}`, string>
>;

export interface EnrollmentConfig extends BannerHeaderFields, BannerBodyFields {
  startsOn?: StartsOnEnum;
  enrollAfter?: number;
  timeUnit?: 'DAYS' | 'WEEKS' | 'MONTHS';
  includeUkPensionLink?: boolean;
  cancellable?: boolean;
  fixedEnrollmentStartDate?: string;
  dynamicEnrollmentStartDate?: string;
  fixedEnrollmentEndDate?: string;
  dynamicEnrollmentEndDate?: DynamicEnrollmentEndDate | '';
  manualEnrollmentEndDate?: boolean;
  resetEnrollmentsOnEndDate?: boolean;
  eligibilityQuery?: string;
}

export interface IAdditionalConfig {
  id?: number;
  subPrices: ISubPrice[];
  subPricesNeedTesting?: boolean;
  steps: IStep[];
  rateTables: IModularBenefitRateTable[];
  tags?: ITag[];
}

export interface AmountField extends IField {
  min: number;
  max: number;
  default: number;
}

export type TextBlock = IComponent;

export interface TextInput extends IField {
  default: number;
  maxLength: number;
  placeholder: string;
}

export interface EligibilityRule {
  id?: number | string;
  query: string;
  usedFields?: string[];
  isActive: boolean;
}

export interface DropdownOption {
  id?: number;
  key: string;
  label: string;
  rank: number;
  eligibilityRule?: EligibilityRule;
}

export interface Dropdown extends IField {
  defaultValue: string;
  placeholder: string;
  searchPlaceholder: string;
  showSearch: boolean;
  fallbackToHighestEligibleOptionRank?: boolean;
  fallbackToLowestEligibleOptionRank?: boolean;
  options: DropdownOption[];
}

export interface Checkbox extends IField {
  default: boolean;
  label: string;
  showLabel: boolean;
}

export interface DateInput extends IField {
  default: Date;
  placeholder: string;
  dateFormat: string;
  allowFutureDates: boolean;
}

export interface PensionSliders extends IField {
  hintText: string;
  employeeDefault: number;
  employeeMin: number;
  employeeMax: number;
  employerMin: number;
  employerMax: number;
}

export enum BuySellHolidayUnitType {
  DAYS = 'DAYS',
  HOURS = 'HOURS',
}

export interface BuySellHoliday extends IField {
  default: number;
  min: number;
  max: number;
  sellMax?: number;
  unitType: BuySellHolidayUnitType;
  interval: number;
}

export interface FileUpload extends IField {
  maxFileSize: string;
  maxFilesNumber: string;
  acceptedTypes: string[];
}

export interface MultiSelectOption {
  id?: number;
  key: string;
  title: string;
  description: string;
  rank: number;
  eligibilityRule?: EligibilityRule;
}

export interface MultiSelect extends IField {
  defaultValue: string[];
  options: MultiSelectOption[];
  minSelections: number;
  maxSelections: number;
  fallbackToHighestEligibleOptionRank?: boolean;
  fallbackToLowestEligibleOptionRank?: boolean;
}

export interface Beneficiaries extends IField {
  acceptedContactTypes: string[];
}

export interface ISubForms {
  components: Component[];
  contactType: string;
}

export interface IContactsComponent extends IField {
  acceptedContactTypes: string[];
  subForms: ISubForms[];
}

export enum SliderValueType {
  NO_SYMBOL = 'NO_SYMBOL',
  PERCENTAGE = 'PERCENTAGE',
}

export interface ISlider extends IField {
  label: string;
  default: number;
  min: number;
  max: number;
  sliderValueType: SliderValueType;
}
export interface ICalculatedValue extends IField {
  label: string;
  valueDescription: string;
  formula: string;
  outputValueType: OutputValueType;
}

export interface IBenefitWindow {
  endsAfterDays?: number | null;
  endsOn?: string;
  id?: number;
  startsOn?: string;
  windowType?: WindowTypeEnum | string;
  windowSubType?: WindowSubTypeEnum | string;
  dateRange?: [Date | null, Date | null];
  endsOnSpecificDate?: string | null;
  startsOnSpecificDate?: string | null;
  startsOnSpecificTime?: string | null;
  endsOnSpecificTime?: string | null;
  fixedEnrolmentStartDate?: string | null;
  /** True when window has been imported from Airtable – not used by the backend */
  importedFromAirtable?: boolean;
  timezone?: string;
  endsAfter?: number | null;
  timeUnit?: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS' | null;
  ageThreshold?: number | null;
  startsAfterDelay?: number | null;
  startsBeforeDays?: number | null;
  applicableEnrolmentStatuses?: string[];
}

export interface LifeEventRestrictionComponent {
  componentId: number;
  type: CustomComponentTypes;
  name: string;
  restrictionApplied: boolean;
  restrictionSet?: RestrictionSet;
  subComponents?: LifeEventRestrictionComponent[];
}

export interface LifeEventRestriction {
  stepId: number;
  components: LifeEventRestrictionComponent[];
}

export interface LifeEventPolicy {
  id?: number;
  type: string[];
  requiresApproval?: boolean;
  requiresProof?: boolean;
  windows?: IBenefitWindow[];
  restrictions?: LifeEventRestriction[];
}

export enum OptOutReasonGroupType {
  WAIVER = 'waiver',
  OPT_OUT = 'opt_out',
}

export interface ConditionalApprovalConfig {
  id?: number;
  name: string;
  approvalFlow?: number;
  triggerFormula: string;
}

export enum ModularBenefitCustomModalType {
  OPT_IN = 'OPT_IN',
  OPT_OUT = 'OPT_OUT',
  CHANGE = 'CHANGE',
}

export interface ModularBenefitCustomModal {
  id?: number;
  titleEn?: string;
  titleFr?: string;
  titleDe?: string;
  titleEs?: string;
  titleEsXl?: string;
  titleSk?: string;
  titleIt?: string;
  titleMs?: string;
  contentEn?: string;
  contentFr?: string;
  contentDe?: string;
  contentEs?: string;
  contentEsXl?: string;
  contentSk?: string;
  contentIt?: string;
  contentMs?: string;
  disclaimerEn?: string;
  disclaimerFr?: string;
  disclaimerDe?: string;
  disclaimerEs?: string;
  disclaimerEsXl?: string;
  disclaimerSk?: string;
  disclaimerIt?: string;
  disclaimerMs?: string;
  ctaTextEn?: string;
  ctaTextFr?: string;
  ctaTextDe?: string;
  ctaTextEs?: string;
  ctaTextEsXl?: string;
  ctaTextSk?: string;
  ctaTextIt?: string;
  ctaTextMs?: string;
  type: ModularBenefitCustomModalType;
}

export type FeatureCategory =
  | 'BENEFIT_TYPE_LIFESTYLE'
  | 'BENEFIT_TYPE_FINANCE'
  | 'BENEFIT_TYPE_FAMILY'
  | 'BENEFIT_TYPE_HEALTH_WELLBEING'
  | 'BENEFIT_TYPE_OTHER';

export interface BenefitFeature {
  id?: number;
  icon: string;
  title: string;
  category: FeatureCategory;
  description: string;
  rank: number;
}

export interface RenewalConfig {
  scheduledRenewalDate: string | null;
  renewalWindowStartDate: string | undefined;
  renewalWindowEndDate: string | undefined;
  hasConfigWindow: boolean;
  hasOptInWindow: boolean;
  hasOptOutWindow: boolean;
}

export interface RenewalConfigFormErrors {
  scheduledRenewalDateError?: boolean;
  renewalWindowDateError?: boolean;
}

export interface RecalculationConfig {
  id?: number;
  sourceField: string;
  frequencyValue: number;
  frequencyUnit: string;
  isActive: boolean;
  preRecalculationMinFormula?: string | null;
  targetUserInputKey?: number | null;
}

type LanguageFields = Partial<
  Record<
    | `name${BenLanguageBackendCode}`
    | `displayName${BenLanguageBackendCode}`
    | `shortDescription${BenLanguageBackendCode}`
    | `content${BenLanguageBackendCode}`
    | `ctaText${BenLanguageBackendCode}`,
    string
  >
>;

export interface IModularBenefitDetail extends LanguageFields {
  id?: number;
  activationDelay: number | string | null;
  allowEmployeePayDeductionChoice?: boolean;
  active?: boolean;
  allowanceImmediateDeduction?: boolean;
  deductionLogic?: string;
  additionalConfig: IAdditionalConfig;
  enrollmentConfig?: EnrollmentConfig | null;
  rank?: number;
  employer?: number | string;
  benefitProgramme?: number | string;
  categories?: ICategory[];
  enrolmentType?: string;
  managedBy?: number;
  benefitManager?: string;
  provider?: number;
  showPrice?: boolean;
  showPaymentSources?: boolean;
  showTotalLoanBanner?: boolean;
  deductionType?: string;
  groupMembershipNumber?: number;
  paymentSource?: string;
  defaultDeductionSource?: DefaultDeductionSourceEnum;
  type?: string;
  externalLinkUrl?: string;
  benefitType?: string;
  newBenefitType?: string;
  benefitRequirements?: string[];
  requiresApproval?: boolean;
  requiresActivationApproval?: boolean;
  requiresDeactivationApproval?: boolean;
  includeInNmwCalculation?: boolean;
  resetDeductionsAtRenewal?: boolean;
  autoActivating?: boolean;
  deactivateEnrolmentsWithProgramme?: boolean;
  benefitWindows?: IBenefitWindow[];
  optOutReasonsGroup?: number;
  ageCalculateFrom?: string;
  salaryCalculateFrom?: string;
  rateTableCalculateFrom?: string;
  currencyOverride?: string;
  closeOptInWindowAfterOptOut?: boolean;
  closeWindowsOnFulfillment?: boolean;
  hideClosedConfigWindowBanner?: boolean;
  documents?: string[] | File[];
  lifeEvents?: string[];
  lifeEventPolicies?: LifeEventPolicy[];
  benefitImage?: {
    benefit?: number;
    id: number;
    image?: string | File;
    imageAltText?: string;
  };
  benefitHeroImage?: {
    benefit?: number;
    id: number;
    image?: string | File;
    imageAltText?: string;
  };
  eligibilityRules: {
    id?: number | string;
    query: string;
    prompt?: string;
    needsTesting?: boolean;
  }[];
  startDate: string | null;
  renewalDate: string | null;
  changeDelay: number | string | null;
  deactivationDelay: number | string | null;
  closeConfigWindowAfterOptIn: boolean;
  keepEnrolledUntilSpa: boolean;
  statePensionAgeConfig: number | null;
  cancelEnrolmentOnOptOutDuringOptInWindow: boolean;
  optInButtonText: string | null;
  clientBenefitCustomCode: string | null;
  optOutReasonsGroupType?: OptOutReasonGroupType;
  conditionalApprovalConfigs?: ConditionalApprovalConfig[];
  broker: string | null;
  showChanges: boolean;
  showEnrolments: boolean;
  showPaymentHistory: boolean;
  prorateForPayroll: boolean;
  remainderAttribution: string;
  postCutoffBehaviour: string;
  pensionChangeField: string;
  startDateOverride?: string;
  startDateOverrideField?: string;
  delayReferenceMode?: string | null;
  endDateOverride?: string;
  endDateOverrideField?: string;
  optInTimestampOverride?: string;
  optOutTimestampOverride?: string;
  setContactOptInToEmployeeOptIn?: boolean;
  enableRequests: boolean;
  status: string | null;
  payDeductionType: string | null;
  recalculatePriceAfterEmployeeChange: boolean;
  recalculatePriceAfterEmployeeSalaryChange: boolean;
  recalculationConfigs?: RecalculationConfig[];
  airtableId?: string;
  sharedBenefit?: {
    id: number;
    managingEntity: {
      id: number;
      name: string;
    };
  };
  customModals?: ModularBenefitCustomModal[];
  features?: BenefitFeature[];
  renewalConfig?: RenewalConfig;
  outboundProvider?: number | null;
  internalNotes?: string;
  product?: number | string;
  policyNumberType?: PolicyNumberType;
  policyLengthMonths?: number | null;
  countries?: string[];
  productType?: number | string;
  isAuditable?: boolean;
}

// Partial object of keys from IModularBenefitDetail and string values
export type IModularBenefitMessages = Omit<
  Partial<Record<keyof IModularBenefitDetail, string>>,
  'benefitWindows'
> & {
  benefitWindows?: Partial<Record<keyof IBenefitWindow, string>>[];
  components?: Partial<Record<keyof IComponent, string>>[];
};

export type Component =
  | AmountField
  | TextBlock
  | TextInput
  | Dropdown
  | Checkbox
  | DateInput
  | PensionSliders
  | BuySellHoliday
  | FileUpload
  | MultiSelect
  | Beneficiaries
  | ICalculatedValue
  | ISlider;
export type Fields =
  | AmountField
  | TextInput
  | Dropdown
  | Checkbox
  | DateInput
  | PensionSliders
  | BuySellHoliday
  | FileUpload
  | MultiSelect
  | Beneficiaries
  | ICalculatedValue
  | ISlider;

export interface ICategory {
  id: number;
  name: string;
  rank: number;
}

export interface ITag {
  id: number;
  name: string;
}
